import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Paginated, StockGroupDto, Unit } from '@eombor/contracts';
import { stockApi } from '../api/stock.api.js';

/**
 * Bir so'rovda olinadigan nomenklatura guruhlari soni — shartnomadagi eng
 * katta qiymat (`paginationQuerySchema`: `limit` max 200).
 */
export const CELL_STOCK_LIMIT = 200;

export interface CellStockEntry {
  nomenclatureId: string;
  code: string;
  name: string;
  unit: Unit;
  /** Katakdagi TO'LIQ qoldiq (band qismi ham shunda) — kirimdan keyingi son shundan hisoblanadi. */
  qty: number;
}

export interface CellStockIndex {
  /** Katak identifikatori → o'sha katakda yotgan nomenklaturalar. */
  byCell: Map<string, CellStockEntry[]>;
  /**
   * Ro'yxat to'liqmi. Server sahifani kesgan bo'lsa `false` — bunda katakni
   * "bo'sh" deb ATAYLAB aytmaymiz: bo'sh emas katakni bo'sh deb ko'rsatish
   * omborchini xato kirimga olib boradi, jim turish esa yo'q.
   */
  complete: boolean;
}

export const EMPTY_CELL_STOCK: CellStockIndex = { byCell: new Map(), complete: false };

/**
 * `GET /stock/search` javobini KATAK bo'yicha indeksga o'giradi.
 *
 * Nega `stock/search`: `GET /stock/by-cell/:id` katakdagi partiyalarni beradi,
 * lekin javobida (`stockPlaceSchema`) NOMENKLATURA yo'q — ya'ni undan
 * "katakda boshqa mahsulot yotibdimi" degan savolga javob chiqmaydi.
 * `search` esa nomenklatura bo'yicha guruhlangan va har guruhda kataklar
 * ro'yxati bor. Yangi endpoint qo'shilmadi.
 */
export const buildCellStockIndex = (page: Paginated<StockGroupDto>): CellStockIndex => {
  const byCell = new Map<string, CellStockEntry[]>();

  for (const group of page.items) {
    for (const place of group.places) {
      const rows = byCell.get(place.cellId) ?? [];
      const existing = rows.find((row) => row.nomenclatureId === group.nomenclatureId);
      // Bitta katakda bitta nomenklaturaning bir necha partiyasi bo'lishi mumkin —
      // omborchiga partiya emas, JAMI qoldiq kerak.
      if (existing === undefined) {
        rows.push({
          nomenclatureId: group.nomenclatureId,
          code: group.code,
          name: group.name,
          unit: group.unit,
          qty: place.qty,
        });
      } else {
        existing.qty += place.qty;
      }
      byCell.set(place.cellId, rows);
    }
  }

  return { byCell, complete: page.items.length >= page.total };
};

export interface CellStockSummary {
  /** Ma'lumot ishonchli — "bo'sh" deyish mumkin. */
  known: boolean;
  /** Shu nomenklaturaning shu katakdagi hozirgi qoldig'i. */
  currentQty: number;
  /** Katakda yotgan BOSHQA nomenklaturalar — inventtalabnomatsiya chalkashishining manbai. */
  others: CellStockEntry[];
}

export const cellStockSummary = (
  index: CellStockIndex,
  cellId: string,
  nomenclatureId: string,
): CellStockSummary => {
  const rows = index.byCell.get(cellId);
  if (rows === undefined) {
    return { known: index.complete, currentQty: 0, others: [] };
  }
  const mine = rows.find((row) => row.nomenclatureId === nomenclatureId);
  return {
    known: true,
    currentQty: mine?.qty ?? 0,
    others: rows.filter((row) => row.nomenclatureId !== nomenclatureId),
  };
};

/**
 * Ombordagi qoldiqning katak kesimi — kirim ekranidagi HAMMA satr shundan
 * o'qiydi. Bitta so'rov: har satr uchun alohida so'rov yuborilsa, o'nta
 * satrli hujjatda o'nta so'rov ketardi.
 */
export const useCellStock = (warehouseId: string): UseQueryResult<CellStockIndex> =>
  useQuery({
    /* Kalit `stock` bilan boshlanadi — kirimdan keyingi
       `invalidateQueries({ queryKey: stockKeys.all })` buni ham yangilaydi.
       `search` dan AYRI nom: bu yerda javob emas, undan yasalgan indeks yotadi. */
    queryKey: ['stock', 'cell-index', warehouseId] as const,
    queryFn: async () =>
      buildCellStockIndex(await stockApi.search({ warehouseId, limit: CELL_STOCK_LIMIT })),
    enabled: warehouseId !== '',
    staleTime: 30_000,
  });
